/**
 * Wearables hooks: healthion-style data flow.
 * Each hook calls apiService.wearables and exposes loading/error/refresh.
 * Use open-health auth (useAuth); API attaches JWT via interceptor.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../auth/AuthContext';
import apiService from '../services/api';

// ---------------------------------------------------------------------------
// Helpers: default date/time ranges
// ---------------------------------------------------------------------------

function getDefaultTimeRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 7);
  return {
    start_time: start.toISOString(),
    end_time: end.toISOString(),
  };
}

function getDefaultDateRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 12000);
  return {
    start_date: start.toISOString().split('T')[0],
    end_date: end.toISOString().split('T')[0],
  };
}

function getPaginationFromResponse(data = {}) {
  const fromNested = data?.pagination || {};
  return {
    next_cursor: fromNested.next_cursor ?? data?.next_cursor ?? null,
    previous_cursor: fromNested.previous_cursor ?? null,
    has_more: fromNested.has_more ?? data?.has_more ?? false,
    total_count: fromNested.total_count ?? data?.count ?? null,
  };
}

// ---------------------------------------------------------------------------
// useWearables: providers + connections, connect, sync, register, refresh
// ---------------------------------------------------------------------------

export function useWearables(options = {}) {
  const { autoFetch = true } = options;
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [providers, setProviders] = useState([]);
  const [connections, setConnections] = useState([]);
  const [openWearablesUserId, setOpenWearablesUserId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasFetched = useRef(false);

  const fetchProviders = useCallback(async () => {
    try {
      const res = await apiService.wearables.getProviders(true, true);
      setProviders(res.data?.providers ?? []);
    } catch (err) {
      console.error('Error fetching providers:', err);
      setError('Failed to fetch providers');
    }
  }, []);

  const fetchConnections = useCallback(async () => {
    try {
      const res = await apiService.wearables.getConnections();
      setConnections(res.data?.connections ?? []);
      setOpenWearablesUserId(res.data?.open_wearables_user_id ?? null);
    } catch (err) {
      console.error('Error fetching connections:', err);
      setError('Failed to fetch connections');
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [providersRes, connectionsRes] = await Promise.all([
        apiService.wearables.getProviders(true, true),
        apiService.wearables.getConnections(),
      ]);
      setProviders(providersRes.data?.providers ?? []);
      setConnections(connectionsRes.data?.connections ?? []);
      setOpenWearablesUserId(connectionsRes.data?.open_wearables_user_id ?? null);
    } catch (err) {
      console.error('Error refreshing:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  const registerWithOpenWearables = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.wearables.register();
      const owId = res.data?.open_wearables_user_id ?? null;
      setOpenWearablesUserId(owId);
      return res.data;
    } catch (err) {
      console.error('Error registering with Open Wearables:', err);
      setError('Failed to register with Open Wearables');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const connectProvider = useCallback(async (providerName, redirectUri) => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.wearables.connectDevice(providerName, redirectUri);
      const url = res.data?.authorization_url;
      if (url) window.location.href = url;
      return res.data;
    } catch (err) {
      console.error('Error connecting provider:', err);
      setError('Failed to connect provider');
      setLoading(false);
      throw err;
    }
  }, []);

  const syncData = useCallback(async (provider, dataType = 'all', opts = {}) => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.wearables.syncData(provider, dataType, opts);
      const data = res.data;
      console.log('Sync response:', data);
      return data;
    } catch (err) {
      console.error('Error syncing data:', err?.response?.data || err);
      setError(err?.response?.data?.detail || 'Failed to sync data');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getLatestSyncRange = useCallback(async (provider) => {
    const res = await apiService.wearables.getSyncHistory(provider, 50);
    const runs = res.data?.runs ?? [];
    const successful = runs.find(
      (run) => run?.provider === provider && run?.status && ['success', 'queued'].includes(run.status.toLowerCase())
    );
    return successful || null;
  }, []);

  const refreshProviderUsingLastRange = useCallback(async (provider) => {
    const lastRun = await getLatestSyncRange(provider);
    if (!lastRun) return null;

    const startDate = lastRun.start_date || null;
    const endDate = lastRun.end_date || new Date().toISOString().split('T')[0];
    const result = await syncData(provider, 'all', { startDate, endDate });
    return {
      ...result,
      usedRange: {
        startDate,
        endDate,
      },
    };
  }, [getLatestSyncRange, syncData]);

  useEffect(() => {
    if (autoFetch && isAuthenticated && !authLoading && !hasFetched.current) {
      hasFetched.current = true;
      refresh();
    }
  }, [autoFetch, isAuthenticated, authLoading, refresh]);

  return {
    providers,
    connections,
    openWearablesUserId,
    loading,
    error,
    registerWithOpenWearables,
    connectProvider,
    syncData,
    getLatestSyncRange,
    refreshProviderUsingLastRange,
    refresh,
    fetchProviders,
    fetchConnections,
  };
}

// ---------------------------------------------------------------------------
// useWearableTimeseries
// ---------------------------------------------------------------------------

export function useWearableTimeseries(options = {}) {
  const autoFetch = options.autoFetch ?? true;
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [data, setData] = useState([]);
  const [count, setCount] = useState(0);
  const [pagination, setPagination] = useState(getPaginationFromResponse());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasFetched = useRef(false);
  const optsRef = useRef(options);
  optsRef.current = options;

  const fetchTimeseries = useCallback(async (customFilters) => {
    setLoading(true);
    setError(null);
    try {
      const defaultRange = getDefaultTimeRange();
      const o = optsRef.current;
      const startTime = customFilters?.start_time ?? o.start_time ?? defaultRange.start_time;
      const endTime = customFilters?.end_time ?? o.end_time ?? defaultRange.end_time;
      const res = await apiService.wearables.getTimeseries(
        startTime,
        endTime,
        customFilters?.types ?? o.types ?? ['heart_rate'],
        customFilters?.limit ?? o.limit ?? 50,
        customFilters?.resolution ?? o.resolution ?? 'raw',
        customFilters?.cursor ?? o.cursor ?? null
      );
      setData(res.data?.data ?? []);
      setCount(res.data?.count ?? 0);
      const nextPagination = getPaginationFromResponse(res.data);
      setPagination(nextPagination);
      return {
        data: res.data?.data ?? [],
        count: res.data?.count ?? 0,
        pagination: nextPagination,
      };
    } catch (err) {
      console.error('Error fetching timeseries:', err);
      setError('Failed to fetch timeseries data');
    } finally {
      setLoading(false);
    }
    return {
      data: [],
      count: 0,
      pagination: getPaginationFromResponse(),
    };
  }, []);

  useEffect(() => {
    if (autoFetch && isAuthenticated && !authLoading && !hasFetched.current) {
      hasFetched.current = true;
      fetchTimeseries();
    }
  }, [autoFetch, isAuthenticated, authLoading, fetchTimeseries]);

  return { data, count, pagination, loading, error, refresh: fetchTimeseries };
}

// ---------------------------------------------------------------------------
// useWearableWorkouts
// ---------------------------------------------------------------------------

export function useWearableWorkouts(options = {}) {
  const { autoFetch = true, limit = 50 } = options;
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [workouts, setWorkouts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasFetched = useRef(false);

  const fetchWorkouts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.wearables.getWorkouts(limit);
      setWorkouts(res.data?.workouts ?? []);
      setTotal(res.data?.total ?? 0);
    } catch (err) {
      console.error('Error fetching workouts:', err);
      setError('Failed to fetch workouts');
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    if (autoFetch && isAuthenticated && !authLoading && !hasFetched.current) {
      hasFetched.current = true;
      fetchWorkouts();
    }
  }, [autoFetch, isAuthenticated, authLoading, fetchWorkouts]);

  return { workouts, total, loading, error, refresh: fetchWorkouts };
}

// ---------------------------------------------------------------------------
// useEventWorkouts
// ---------------------------------------------------------------------------

export function useEventWorkouts(options = {}) {
  const autoFetch = options.autoFetch ?? true;
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [workouts, setWorkouts] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [pagination, setPagination] = useState(getPaginationFromResponse());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasFetched = useRef(false);
  const optsRef = useRef(options);
  optsRef.current = options;

  const fetchWorkouts = useCallback(async (filters) => {
    setLoading(true);
    setError(null);
    try {
      const defaultRange = getDefaultDateRange();
      const o = optsRef.current;
      const start = filters?.start_date ?? o.startDate ?? defaultRange.start_date;
      const end = filters?.end_date ?? o.endDate ?? defaultRange.end_date;
      const res = await apiService.wearables.getEventWorkouts(
        start,
        end,
        null,
        filters?.limit ?? o.limit ?? 50,
        filters?.cursor ?? o.cursor ?? null
      );
      setWorkouts(res.data?.data ?? []);
      const nextPagination = getPaginationFromResponse(res.data);
      setPagination(nextPagination);
      setHasMore(nextPagination.has_more);
    } catch (err) {
      console.error('Error fetching event workouts:', err);
      setError('Failed to fetch workouts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (autoFetch && isAuthenticated && !authLoading && !hasFetched.current) {
      hasFetched.current = true;
      fetchWorkouts();
    }
  }, [autoFetch, isAuthenticated, authLoading, fetchWorkouts]);

  return { workouts, hasMore, pagination, loading, error, refresh: fetchWorkouts };
}

// ---------------------------------------------------------------------------
// useSleepSessions
// ---------------------------------------------------------------------------

export function useSleepSessions(options = {}) {
  const autoFetch = options.autoFetch ?? true;
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [pagination, setPagination] = useState(getPaginationFromResponse());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasFetched = useRef(false);
  const optsRef = useRef(options);
  optsRef.current = options;

  const fetchSessions = useCallback(async (filters) => {
    setLoading(true);
    setError(null);
    try {
      const defaultRange = getDefaultDateRange();
      const o = optsRef.current;
      const start = filters?.start_date ?? o.startDate ?? defaultRange.start_date;
      const end = filters?.end_date ?? o.endDate ?? defaultRange.end_date;
      const res = await apiService.wearables.getSleepSessions(
        start,
        end,
        filters?.limit ?? o.limit ?? 50,
        filters?.cursor ?? o.cursor ?? null
      );
      setSessions(res.data?.data ?? []);
      const nextPagination = getPaginationFromResponse(res.data);
      setPagination(nextPagination);
      setHasMore(nextPagination.has_more);
    } catch (err) {
      console.error('Error fetching sleep sessions:', err);
      setError('Failed to fetch sleep sessions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (autoFetch && isAuthenticated && !authLoading && !hasFetched.current) {
      hasFetched.current = true;
      fetchSessions();
    }
  }, [autoFetch, isAuthenticated, authLoading, fetchSessions]);

  return { sessions, hasMore, pagination, loading, error, refresh: fetchSessions };
}

// ---------------------------------------------------------------------------
// useActivitySummary
// ---------------------------------------------------------------------------

export function useActivitySummary(options = {}) {
  const autoFetch = options.autoFetch ?? true;
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [summaries, setSummaries] = useState([]);
  const [pagination, setPagination] = useState(getPaginationFromResponse());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasFetched = useRef(false);
  const optsRef = useRef(options);
  optsRef.current = options;

  const fetchSummary = useCallback(async (filters) => {
    setLoading(true);
    setError(null);
    try {
      const defaultRange = getDefaultDateRange();
      const o = optsRef.current;
      const start = filters?.start_date ?? o.startDate ?? defaultRange.start_date;
      const end = filters?.end_date ?? o.endDate ?? defaultRange.end_date;
      const res = await apiService.wearables.getActivitySummary(
        start,
        end,
        filters?.limit ?? o.limit ?? 50,
        filters?.cursor ?? o.cursor ?? null
      );
      setSummaries(res.data?.data ?? []);
      setPagination(getPaginationFromResponse(res.data));
    } catch (err) {
      console.error('Error fetching activity summary:', err);
      setError('Failed to fetch activity summary');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (autoFetch && isAuthenticated && !authLoading && !hasFetched.current) {
      hasFetched.current = true;
      fetchSummary();
    }
  }, [autoFetch, isAuthenticated, authLoading, fetchSummary]);

  return { summaries, pagination, loading, error, refresh: fetchSummary };
}

// ---------------------------------------------------------------------------
// useSleepSummary
// ---------------------------------------------------------------------------

export function useSleepSummary(options = {}) {
  const autoFetch = options.autoFetch ?? true;
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [summaries, setSummaries] = useState([]);
  const [pagination, setPagination] = useState(getPaginationFromResponse());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasFetched = useRef(false);
  const optsRef = useRef(options);
  optsRef.current = options;

  const fetchSummary = useCallback(async (filters) => {
    setLoading(true);
    setError(null);
    try {
      const defaultRange = getDefaultDateRange();
      const o = optsRef.current;
      const start = filters?.start_date ?? o.startDate ?? defaultRange.start_date;
      const end = filters?.end_date ?? o.endDate ?? defaultRange.end_date;
      const res = await apiService.wearables.getSleepSummary(
        start,
        end,
        filters?.limit ?? o.limit ?? 50,
        filters?.cursor ?? o.cursor ?? null
      );
      setSummaries(res.data?.data ?? []);
      setPagination(getPaginationFromResponse(res.data));
    } catch (err) {
      console.error('Error fetching sleep summary:', err);
      setError('Failed to fetch sleep summary');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (autoFetch && isAuthenticated && !authLoading && !hasFetched.current) {
      hasFetched.current = true;
      fetchSummary();
    }
  }, [autoFetch, isAuthenticated, authLoading, fetchSummary]);

  return { summaries, pagination, loading, error, refresh: fetchSummary };
}

// ---------------------------------------------------------------------------
// useRecoverySummary
// ---------------------------------------------------------------------------

export function useRecoverySummary(options = {}) {
  const autoFetch = options.autoFetch ?? true;
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [summaries, setSummaries] = useState([]);
  const [pagination, setPagination] = useState(getPaginationFromResponse());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasFetched = useRef(false);
  const optsRef = useRef(options);
  optsRef.current = options;

  const fetchSummary = useCallback(async (filters) => {
    setLoading(true);
    setError(null);
    try {
      const defaultRange = getDefaultDateRange();
      const o = optsRef.current;
      const start = filters?.start_date ?? o.startDate ?? defaultRange.start_date;
      const end = filters?.end_date ?? o.endDate ?? defaultRange.end_date;
      const res = await apiService.wearables.getRecoverySummary(
        start,
        end,
        filters?.limit ?? o.limit ?? 50,
        filters?.cursor ?? o.cursor ?? null
      );
      setSummaries(res.data?.data ?? []);
      setPagination(getPaginationFromResponse(res.data));
    } catch (err) {
      console.error('Error fetching recovery summary:', err);
      setError('Failed to fetch recovery summary');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (autoFetch && isAuthenticated && !authLoading && !hasFetched.current) {
      hasFetched.current = true;
      fetchSummary();
    }
  }, [autoFetch, isAuthenticated, authLoading, fetchSummary]);

  return { summaries, pagination, loading, error, refresh: fetchSummary };
}

// ---------------------------------------------------------------------------
// useBodySummary
// ---------------------------------------------------------------------------

export function useBodySummary(options = {}) {
  const autoFetch = options.autoFetch ?? true;
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasFetched = useRef(false);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.wearables.getBodySummary();
      setSummaries(res.data?.data ?? []);
    } catch (err) {
      console.error('Error fetching body summary:', err);
      setError('Failed to fetch body summary');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (autoFetch && isAuthenticated && !authLoading && !hasFetched.current) {
      hasFetched.current = true;
      fetchSummary();
    }
  }, [autoFetch, isAuthenticated, authLoading, fetchSummary]);

  return { summaries, loading, error, refresh: fetchSummary };
}

// ---------------------------------------------------------------------------
// useWorkoutDetail
// ---------------------------------------------------------------------------

export function useWorkoutDetail(provider, workoutId, options = {}) {
  const { autoFetch = true } = options;
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [workout, setWorkout] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasFetched = useRef(false);

  const fetchWorkout = useCallback(async () => {
    if (!provider || !workoutId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.wearables.getWorkoutDetail(provider, workoutId);
      setWorkout(res.data ?? null);
    } catch (err) {
      console.error('Error fetching workout detail:', err);
      setError('Failed to fetch workout detail');
    } finally {
      setLoading(false);
    }
  }, [provider, workoutId]);

  useEffect(() => {
    if (autoFetch && isAuthenticated && !authLoading && provider && workoutId && !hasFetched.current) {
      hasFetched.current = true;
      fetchWorkout();
    }
  }, [autoFetch, isAuthenticated, authLoading, fetchWorkout, provider, workoutId]);

  return { workout, loading, error, refresh: fetchWorkout };
}

// ---------------------------------------------------------------------------
// useAvailableSeriesTypes
// ---------------------------------------------------------------------------

export function useAvailableSeriesTypes(options = {}) {
  const { autoFetch = true } = options;
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasFetched = useRef(false);

  const fetchTypes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.wearables.getSeriesTypes();
      setTypes(res.data?.types ?? []);
    } catch (err) {
      console.error('Error fetching series types:', err);
      setError('Failed to fetch available series types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (autoFetch && isAuthenticated && !authLoading && !hasFetched.current) {
      hasFetched.current = true;
      fetchTypes();
    }
  }, [autoFetch, isAuthenticated, authLoading, fetchTypes]);

  return { types, loading, error, refresh: fetchTypes };
}

// ---------------------------------------------------------------------------
// useAppleHealthImport
// ---------------------------------------------------------------------------

export function useAppleHealthImport() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const importAppleHealth = useCallback(async (fileKey) => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await apiService.wearables.importAppleHealth(fileKey);
      setResult(res.data ?? null);
      return res.data;
    } catch (err) {
      console.error('Error importing Apple Health data:', err);
      setError('Failed to import Apple Health data');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { importAppleHealth, loading, error, result };
}
